import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEOHead';

export default function PrivacyPolicyPage() {
  const { t } = useTranslation();

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'AnalizaPrawna', item: 'https://analizaprawna.eu/' },
      { '@type': 'ListItem', position: 2, name: t('legal.privacyPolicy.title') },
    ],
  };

  return (
    <div className="max-w-4xl mx-auto">
      <SEOHead
        title={`${t('legal.privacyPolicy.title')} | AnalizaPrawna`}
        description={t('legal.privacyPolicy.metaDescription')}
        canonicalUrl="https://analizaprawna.eu/polityka-prywatnosci"
        jsonLd={breadcrumbLd}
      />

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-8 md:p-12">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
          {t('legal.privacyPolicy.title')}
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-8">
          {t('legal.privacyPolicy.lastUpdated')}
        </p>

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
          {/* Section 1 - General */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.privacyPolicy.sections.general.title')}
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.privacyPolicy.sections.general.p1')}</li>
              <li>{t('legal.privacyPolicy.sections.general.p2')}</li>
              <li>{t('legal.privacyPolicy.sections.general.p3')}</li>
            </ol>
          </section>

          {/* Section 2 - Data Controller */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.privacyPolicy.sections.controller.title')}
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.privacyPolicy.sections.controller.p1')}</li>
              <li>{t('legal.privacyPolicy.sections.controller.p2')}</li>
            </ol>
          </section>

          {/* Section 3 - Data Processing Purposes */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.privacyPolicy.sections.purposes.title')}
            </h2>
            <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed mb-3">
              {t('legal.privacyPolicy.sections.purposes.intro')}
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.privacyPolicy.sections.purposes.p1')}</li>
              <li>{t('legal.privacyPolicy.sections.purposes.p2')}</li>
              <li>{t('legal.privacyPolicy.sections.purposes.p3')}</li>
              <li>{t('legal.privacyPolicy.sections.purposes.p4')}</li>
              <li>{t('legal.privacyPolicy.sections.purposes.p5')}</li>
            </ol>
          </section>

          {/* Section 4 - Legal Basis */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.privacyPolicy.sections.legalBasis.title')}
            </h2>
            <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed mb-3">
              {t('legal.privacyPolicy.sections.legalBasis.intro')}
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.privacyPolicy.sections.legalBasis.p1')}</li>
              <li>{t('legal.privacyPolicy.sections.legalBasis.p2')}</li>
              <li>{t('legal.privacyPolicy.sections.legalBasis.p3')}</li>
              <li>{t('legal.privacyPolicy.sections.legalBasis.p4')}</li>
            </ol>
          </section>

          {/* Section 5 - Data Scope */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.privacyPolicy.sections.dataScope.title')}
            </h2>
            <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed mb-3">
              {t('legal.privacyPolicy.sections.dataScope.intro')}
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.privacyPolicy.sections.dataScope.p1')}</li>
              <li>{t('legal.privacyPolicy.sections.dataScope.p2')}</li>
              <li>{t('legal.privacyPolicy.sections.dataScope.p3')}</li>
              <li>{t('legal.privacyPolicy.sections.dataScope.p4')}</li>
            </ol>
          </section>

          {/* Section 6 - Data Retention */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.privacyPolicy.sections.retention.title')}
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.privacyPolicy.sections.retention.p1')}</li>
              <li>{t('legal.privacyPolicy.sections.retention.p2')}</li>
              <li>{t('legal.privacyPolicy.sections.retention.p3')}</li>
              <li>{t('legal.privacyPolicy.sections.retention.p4')}</li>
            </ol>
          </section>

          {/* Section 7 - User Rights */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.privacyPolicy.sections.rights.title')}
            </h2>
            <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed mb-3">
              {t('legal.privacyPolicy.sections.rights.intro')}
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.privacyPolicy.sections.rights.p1')}</li>
              <li>{t('legal.privacyPolicy.sections.rights.p2')}</li>
              <li>{t('legal.privacyPolicy.sections.rights.p3')}</li>
              <li>{t('legal.privacyPolicy.sections.rights.p4')}</li>
              <li>{t('legal.privacyPolicy.sections.rights.p5')}</li>
              <li>{t('legal.privacyPolicy.sections.rights.p6')}</li>
              <li>{t('legal.privacyPolicy.sections.rights.p7')}</li>
            </ol>
          </section>

          {/* Section 8 - Data Recipients */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.privacyPolicy.sections.recipients.title')}
            </h2>
            <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed mb-3">
              {t('legal.privacyPolicy.sections.recipients.intro')}
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.privacyPolicy.sections.recipients.p1')}</li>
              <li>{t('legal.privacyPolicy.sections.recipients.p2')}</li>
              <li>{t('legal.privacyPolicy.sections.recipients.p3')}</li>
            </ol>
          </section>

          {/* Section 9 - Cookies */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.privacyPolicy.sections.cookies.title')}
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.privacyPolicy.sections.cookies.p1')}</li>
              <li>{t('legal.privacyPolicy.sections.cookies.p2')}</li>
              <li>{t('legal.privacyPolicy.sections.cookies.p3')}</li>
            </ol>
          </section>

          {/* Section 10 - Security */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.privacyPolicy.sections.security.title')}
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.privacyPolicy.sections.security.p1')}</li>
              <li>{t('legal.privacyPolicy.sections.security.p2')}</li>
            </ol>
          </section>

          {/* Section 11 - Changes */}
          <section>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
              {t('legal.privacyPolicy.sections.changes.title')}
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <li>{t('legal.privacyPolicy.sections.changes.p1')}</li>
              <li>{t('legal.privacyPolicy.sections.changes.p2')}</li>
            </ol>
          </section>
        </div>
      </div>
    </div>
  );
}
